import type { ProjectContributor } from '@dyspatch/core';
import { ExternalLink, GitBranch, Github } from 'lucide-react';
import { initials } from '@/lib/format';

/**
 * What a repo is, above the numbers (issue #68), rebuilt for issue #101.
 *
 * `ViewHeader` already carries the repo's name (the page title) and its last
 * activity (the clause under it), so this card no longer repeats either one.
 * It says only what the page header cannot: the description (or an invitation
 * to add one), the facts that place the repo, and who works on it.
 *
 * The old card built three role-keyed lists out of `contributors` and joined
 * the names in each with commas, which is why the same person showed up in
 * every list they had a role in. `contributors` already carries one entry per
 * person (`projectContributors` in `packages/hub/src/routes/views.ts` keys it
 * by actor before this ever reaches the browser), so the fix is to stop
 * re-grouping it by role and render it the way it already is: one row per
 * person, their roles carried alongside their name rather than split across
 * three lists.
 */

/** The roles a contributor holds, in a fixed order, joined for the line under their name. */
function rolesOf(contributor: ProjectContributor): string {
  const roles: string[] = [];
  if (contributor.filed) roles.push('filed');
  if (contributor.approved) roles.push('approved');
  if (contributor.reviewed) roles.push('reviewed');
  return roles.join(', ');
}

function ContributorRow({ contributor }: { contributor: ProjectContributor }) {
  const roles = rolesOf(contributor);
  return (
    <div className="flex min-w-0 items-center gap-2.5" title={roles || undefined}>
      <span
        className="bg-raised text-fg-2 grid size-7 shrink-0 place-items-center rounded-full text-2xs font-semibold"
        aria-hidden
      >
        {initials(contributor.actor)}
      </span>
      <div className="min-w-0">
        <div className="truncate text-sm">{contributor.actor}</div>
        {roles ? <div className="text-fg-3 truncate text-2xs">{roles}</div> : null}
      </div>
    </div>
  );
}

export function ProjectHeader({
  repo,
  description,
  defaultBranch,
  githubUrl,
  contributors,
}: {
  repo: string;
  description: string | null;
  defaultBranch: string | null;
  githubUrl: string;
  contributors: readonly ProjectContributor[];
}) {
  return (
    <section className="dy-surface-2 px-5 py-5 sm:px-7 sm:py-6" aria-label={`About ${repo}`}>
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0 max-w-[60ch]">
          {description ? (
            <p className="text-fg-2 text-sm">{description}</p>
          ) : (
            <a
              href={githubUrl}
              target="_blank"
              rel="noreferrer"
              className="text-fg-2 inline-flex min-w-0 items-center gap-1.5 text-sm hover:underline"
            >
              Add a description on GitHub
              <ExternalLink className="size-3.5 shrink-0 opacity-70" aria-hidden />
            </a>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 text-xs">
          {defaultBranch ? (
            <span className="bg-raised text-fg-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium">
              <GitBranch className="size-3" aria-hidden />
              {defaultBranch}
            </span>
          ) : null}
          <a
            href={githubUrl}
            target="_blank"
            rel="noreferrer"
            className="bg-raised text-fg-2 inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium hover:underline"
          >
            <Github className="size-3" aria-hidden />
            View on GitHub
            <ExternalLink className="size-3 shrink-0 opacity-70" aria-hidden />
          </a>
        </div>
      </div>

      {contributors.length ? (
        <div className="mt-4">
          <div className="dy-eyebrow">Contributors · {contributors.length}</div>
          <div className="mt-2.5 flex flex-wrap gap-x-6 gap-y-3">
            {contributors.map((contributor) => (
              <ContributorRow key={contributor.actor} contributor={contributor} />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
